import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { SidebarSimpleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { PricelistComparisonPanel } from './PricelistComparisonPanel';
import { usePanelOpenOutsideFocus } from './reviewFocus';

/**
 * Another collapsible panel beside the pricelist panel, with its own tab under
 * the Pricelists tab. The host owns its open state.
 */
export interface LayoutSidePanel {
  /** The resizable panel's id. */
  id: string;
  /** The vertical tab's label. */
  label: ReactNode;
  /** The vertical tab's accessible name. */
  ariaLabel: string;
  /** Shown on the tab when above zero (e.g. the open comment threads). */
  count?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

/**
 * Hosts a page next to the collapsible, resizable pricelist comparison panel —
 * the query-builder sidebar pattern. The children render in the main panel and
 * must own their vertical scrolling; an always-visible vertical tab strip on
 * the right edge opens and closes the panel, closed by default. `sidePanels`
 * adds more panels the same way (the review page's comments panel).
 */
export function PricelistComparisonLayout({
  children,
  sidePanels = [],
}: {
  children: ReactNode;
  sidePanels?: readonly LayoutSidePanel[];
}) {
  const gt = useGT();
  const [panelOpen, setPanelOpen] = usePanelOpenOutsideFocus(false);

  return (
    // `flex-1 min-w-0` so the layout shares a flex row with a sibling sidebar
    // (the review page's actions sidebar) instead of pushing it off screen.
    <div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden">
      <ResizablePanelGroup
        direction="horizontal"
        className="flex-1 overflow-hidden"
      >
        <ResizablePanel id="pricelist-compare-main" order={1} minSize={40}>
          {children}
        </ResizablePanel>

        {panelOpen && (
          <>
            <ResizableHandle withHandle className="mx-1" />
            <ResizablePanel
              id="pricelist-compare-panel"
              order={2}
              defaultSize={35}
              minSize={20}
              maxSize={60}
            >
              <PricelistComparisonPanel />
            </ResizablePanel>
          </>
        )}

        {sidePanels.map(
          (panel, index) =>
            panel.open && (
              <SidePanelSlot key={panel.id} panel={panel} order={3 + index} />
            ),
        )}
      </ResizablePanelGroup>

      {/* Always-visible vertical tab strip on the right edge. */}
      <div className="flex shrink-0 flex-col border-l border-border bg-muted/40">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-full shrink-0 rounded-none"
          onClick={() => setPanelOpen((open) => !open)}
          aria-label={
            panelOpen
              ? gt('Collapse pricelist panel', {
                  context: 'aria label for the pricelist panel toggle button',
                })
              : gt('Expand pricelist panel', {
                  context: 'aria label for the pricelist panel toggle button',
                })
          }
        >
          <SidebarSimpleIcon className="h-4 w-4" />
        </Button>
        <div className="h-px bg-border" />
        <VerticalPanelTab
          open={panelOpen}
          onToggle={() => setPanelOpen((open) => !open)}
          ariaLabel={gt('Pricelists panel', {
            context: 'aria label for the pricelist comparison vertical tab',
          })}
        >
          <T context="vertical tab label for the pricelist comparison panel">
            Pricelists
          </T>
        </VerticalPanelTab>
        {sidePanels.map((panel) => (
          <VerticalPanelTab
            key={panel.id}
            open={panel.open}
            onToggle={() => panel.onOpenChange(!panel.open)}
            ariaLabel={panel.ariaLabel}
            count={panel.count}
          >
            {panel.label}
          </VerticalPanelTab>
        ))}
      </div>
    </div>
  );
}

function SidePanelSlot({
  panel,
  order,
}: {
  panel: LayoutSidePanel;
  order: number;
}) {
  return (
    <>
      <ResizableHandle withHandle className="mx-1" />
      <ResizablePanel
        id={panel.id}
        order={order}
        defaultSize={25}
        minSize={15}
        maxSize={50}
      >
        {panel.children}
      </ResizablePanel>
    </>
  );
}

/** One tab of the strip: toggles its panel, marked while the panel is open. */
function VerticalPanelTab({
  open,
  onToggle,
  ariaLabel,
  count,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  ariaLabel: string;
  count?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={open}
      className={cn(
        'relative flex cursor-pointer flex-col items-center justify-center gap-1.5 px-2 py-4 transition-colors',
        open
          ? 'bg-card text-primary after:absolute after:left-0 after:top-0 after:h-full after:w-0.5 after:bg-primary'
          : 'text-muted-foreground hover:text-foreground',
      )}
      aria-label={ariaLabel}
    >
      {count !== undefined && count > 0 && (
        <span className="min-w-4 rounded-full bg-primary px-1 text-center text-[10px] leading-4 font-semibold text-primary-foreground tabular-nums">
          {count}
        </span>
      )}
      <span
        className="select-none text-xs font-medium"
        style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
        }}
      >
        {children}
      </span>
    </button>
  );
}
